import styled from "@emotion/styled";
import { differenceInDays } from "date-fns";
import { useNavigate } from "react-router-dom";
import { Notice } from "../../types/types";
import userStorage from "../../utils/userStorage";
import theme from "../../theme";
import { ExclamationMark } from "../../assets/icons/icons";
import useGetNotice from "../../hooks/useGetNotice";
import { useEffect, useState } from "react";
import {
  formatDate,
  handleKeyDown,
  textSubstring,
} from "../../utils/util";
import { useI18n } from "../../locales";

const { md } = theme.screen;

interface NoticeListProps {
  item: Notice;
}

const NoticeList: React.FC<NoticeListProps> = (props) => {
  const { item } = props;
  const { t } = useI18n();

  const navigate = useNavigate();

  const onClickNotice = (no: number) => {
    navigate(`/notice/${no}`);
  };

  const user = userStorage.get();
  const isAdmin = user?.grade === 2;
  const isNew = differenceInDays(new Date(), new Date(item.create_dt)) < 1;
  const [notRead, setNotRead] = useState(false);

  const { data: notReadNoticeData } = useGetNotice(0, 0, -1); //(page, newCheck, limit) 공지리스트 전체 조회
  // console.log(notReadNoticeData);
  // console.log(item);

  useEffect(() => {
    if (notReadNoticeData) {
      if (
        notReadNoticeData.list.length > 0 &&
        notReadNoticeData.list.findIndex((el) => el.idx === item.idx) !==
          -1
      ) {
        setNotRead(true);
      } else {
        setNotRead(false);
      }
    }
  }, [notReadNoticeData]);

  return (
    <NoticeListWrapper
      onClick={() => onClickNotice(item.idx)}
      isAdmin={isAdmin}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <LeftWrapper>
        {/* WEB/APP/신규 아이콘 부분 */}
        <BtnWrapper>
          {/* 유저가 확인하지 않은 공지 체크 - 느낌표 */}
          {notRead && (
            <ExclamationMarkContainer>
              <ExclamationMark />
            </ExclamationMarkContainer>
          )}
          {isAdmin ? (
            item.part === 2 ? (
              <AppBtn>{t.notice.app}</AppBtn>
            ) : (
              <WebBtn>{t.notice.web}</WebBtn>
            )
          ) : null}

          <NewButton isNew={isNew}>
            <NewButtonText>{t.notice.new}</NewButtonText>
          </NewButton>
        </BtnWrapper>

        {/* 타이틀 부분 */}
        <NoticeTitle>{textSubstring(item.title, 0, 65)}</NoticeTitle>
      </LeftWrapper>

      {/* 날짜 부분 */}
      <NoticeDate>
        {formatDate(item.create_dt, "yyyy-MM-dd HH:mm:ss")}
      </NoticeDate>
    </NoticeListWrapper>
  );
};

const NoticeListWrapper = styled.div<{ isAdmin: boolean }>`
  height: 50px;
  position: relative;
  cursor: pointer;
  background: ${theme.color.white};
  border-radius: 10px;
  padding-left: 40px;
  padding-right: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  &:hover {
    background-color: ${theme.color.neutral_100};
  }

  @media (max-width: ${md}px) {
    height: auto;
    min-height: 45px;
    padding-left: 20px;
    padding-right: 10px;
    padding-top: 8px;
    padding-bottom: 8px;
    align-items: flex-start;
  }
`;

const LeftWrapper = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;

  @media (max-width: ${md}px) {
    width: auto;
    flex: 1;
    min-width: 0;
  }
`;

const NoticeTitle = styled.h1`
  font-weight: 500;
  font-size: 16px;
  line-height: 19px;
  color: ${theme.color.neutral_600};
  margin-left: 14px;

  @media (max-width: ${md}px) {
    flex-grow: 1;
    font-size: 14px;
    line-height: 17px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
`;

const NoticeDate = styled.p`
  font-weight: 400;
  font-size: 14px;
  line-height: 50px;
  text-align: center;
  color: ${theme.color.neutral_500};
  white-space: nowrap;
  flex-shrink: 0;

  @media (max-width: ${md}px) {
    font-size: 10px;
    line-height: 18px;
    white-space: normal;
    word-break: break-word;
    text-align: right;
    min-width: 60px;
  }
`;

const NewButton = styled.div<{ isNew: boolean }>`
  width: 35.4px;
  height: 18.8px;
  background: ${theme.statusColor.MAIN};
  border-radius: 4.75042px;
  align-items: center;
  justify-content: center;
  display: ${({ isNew }) => (isNew ? "flex" : "none")};

  @media (max-width: ${md}px) {
    width: 30px;
    height: 17px;
  }
`;

const NewButtonText = styled.span`
  font-weight: 400;
  font-size: 13px;
  line-height: 15px;
  color: ${theme.color.white};
  padding-top: 1px; // 글씨가 살짝 위로 치우쳐서 패딩 줌

  @media (max-width: ${md}px) {
    font-size: 10px;
    line-height: 12px;
    padding-top: 0px;
  }
`;

const WebBtn = styled.div`
  width: 39.4px;
  height: 18.8px;
  background: ${theme.color.neutral_500};
  border-radius: 4.75042px;
  font-weight: 400;
  font-size: 13px;
  line-height: 15px;
  color: ${theme.color.white};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ${md}px) {
    width: 30px;
    height: 17px;
    font-size: 10px;
    line-height: 12px;
  }
`;

const AppBtn = styled.div`
  width: 36.4px;
  height: 18.8px;
  background: ${theme.color.neutral_200};
  border-radius: 4.75042px;
  font-weight: 400;
  font-size: 13px;
  line-height: 15px;
  color: ${theme.color.neutral_800};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ${md}px) {
    width: 30px;
    height: 17px;
    font-size: 10px;
    line-height: 12px;
  }
`;

const BtnWrapper = styled.div`
  display: flex;
  gap: 5.8px;
`;

const ExclamationMarkContainer = styled.div`
  position: absolute;
  left: 10px;

  @media (max-width: ${md}px) {
    left: 2px;
    svg {
      height: 17px;
      width: 17px;
    }
  }
`;

export default NoticeList;
